import {
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  TemplateRef
} from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { PopupService } from "src/app/services/popup/popup.service";
import { sntTranslate } from "src/app/support/translate";
import { DxSortableTypes } from "devextreme-angular/ui/sortable";
import {
  ReportDesignerItemTab,
  ReportDesignerItemTabConfig,
  ReportDesignerItemTabItem
} from "../../report-design-item";
import { compact } from "lodash-es";
import { ReportDesignerComponent } from "src/app/modules/bootstrap/modules/report/report-designer/report-designer.component";
import { sntConfirm } from "src/app/common/dialog";

@Component({
  selector: "snt-report-designer-tab",
  templateUrl: "report-designer-tab.component.html",
  styles: [
    `
      :host {
        display: flex;
        flex: 1;
      }
    `
  ],
  standalone: false
})
export class ReportDesignerTabComponent {
  @Input()
  item!: ReportDesignerItemTab;

  @Input()
  templateOutlet!: TemplateRef<any>;

  @Input()
  parentName!: string;

  @Input()
  level!: number;

  @Input()
  readonly: boolean = false;

  @Input()
  disableRemoveWarning: boolean | undefined;

  @Output() config: EventEmitter<ReportDesignerItemTabConfig> =
    new EventEmitter();

  selectedIndex = 0;

  private popupService = inject(PopupService);
  private reportDesginer = inject(ReportDesignerComponent);

  onTitleChange(value: string): void {
    this.item.config.title = value;
    this.config.emit(this.item.config);
  }

  onTabNameChange(tab: ReportDesignerItemTabItem, value: string): void {
    tab.name = value;
    this.config.emit(this.item.config);
  }

  onTabDragStart(e: DxSortableTypes.DragStartEvent): void {
    e.itemData = e.fromData[e.fromIndex];
  }

  onTabsReorder(e: DxSortableTypes.ReorderEvent): void {
    e.fromData.splice(e.fromIndex, 1);
    e.toData.splice(e.toIndex, 0, e.itemData);

    this.item.config.tabs.forEach((tab, i) => {
      tab.index = i;
    });

    const fromIndex = e.fromIndex;
    const toIndex = e.toIndex;
    if (fromIndex === this.selectedIndex) {
      this.selectedIndex = toIndex;
    } else if (
      fromIndex < this.selectedIndex &&
      toIndex >= this.selectedIndex
    ) {
      this.selectedIndex--;
    } else if (
      fromIndex > this.selectedIndex &&
      toIndex <= this.selectedIndex
    ) {
      this.selectedIndex++;
    }

    this.config.emit(this.item.config);
  }

  async onRemove(tab: ReportDesignerItemTabItem): Promise<void> {
    if (!this.disableRemoveWarning) {
      const result = await sntConfirm(
        sntTranslate(marker("Editor schede visita")),
        sntTranslate(
          marker(
            "La rimozione del tab comporterà la perdita dei valori inseriti nelle schede visita già create. Vuoi proseguire?"
          )
        )
      );

      if (!result) {
        return;
      }
    }

    this.item.config.tabs = this.item.config.tabs.filter((x) => x !== tab);

    this.item.config.tabs.forEach((t, index) => {
      t.index = index;
    });

    const maxIndex = Math.max(0, this.item.config.tabs.length - 1);
    this.selectedIndex = Math.min(this.selectedIndex, maxIndex);

    this.config.emit(this.item.config);
  }

  async onAdd(): Promise<void> {
    await this.popupService.createEntity<{ name: string }>({
      title: sntTranslate(marker("Nuovo tab")),
      modalConfig: {
        dialog: true,
        height: 120
      },
      formItems: [
        {
          dataField: "name",
          label: {
            text: sntTranslate(marker("Nome")),
            location: "top",
            showColon: false
          },
          validationRules: [{ type: "required" }]
        }
      ],
      create: async (data) => {
        this.item.config.tabs.push({
          name: data.name,
          items: [],
          index: this.item.config.tabs.length
        });

        this.config.emit(this.item.config);
      }
    });
  }

  async renderPdfObject(): Promise<Record<string, any>> {
    const allRenderers = this.reportDesginer.renderers.toArray();

    const filteredRenderers = allRenderers.filter(
      (x) =>
        x.level === this.level + 1 && x.parentName === this.item.config.name
    );

    const renderedItems = compact(
      await Promise.all(
        filteredRenderers.map((x) => {
          return !!x.renderPdfObject
            ? x.renderPdfObject().then((rendered: any) => {
                return { name: x.item?.config?.name || "", rendered };
              })
            : Promise.resolve(null);
        })
      )
    );

    return {
      type: "tab",
      config: {
        name: this.item.config.name,
        tabs: this.item.config.tabs.map((tab) => {
          return {
            title: tab.name,
            items: renderedItems
          };
        })
      }
    };
  }
}
